/**
 * src/components/shared/TaskDetailPanel.jsx — Shared Task Detail Panel
 * AC Nautitech Executive Assistant
 *
 * Consolidated task detail panel used across Gantt, Pipeline, and Team views.
 * Fully prop-driven — no direct API imports. Parent wraps in ModalSheet.
 *
 * Sub-panels extracted for maintainability:
 *   - TaskDetailsForm.jsx  — budget hours, dates, assignee/project, department/bundle
 *   - TaskNotesSection.jsx — notes, materials, voice notes, photo upload
 *   - TaskTimeLogSection.jsx — time log form + history
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { CheckSquare, FileText, Trash2, X } from 'lucide-react';
import { useAppDataContext } from '../../lib/AppDataContext';
import { requestQuoteOpen } from '../../lib/nav/quoteNav';
import { StageBadge } from '../SharedUI';
import { useT } from '../../lib/i18n/I18nContext';
import { useContentLanguage } from '../../lib/i18n/useContentLanguage';
import { useLocalizedRows } from '../../lib/i18n/useLocalizedRows';
import ContentLangToggle from './ContentLangToggle';
import { priorityToP, pToPriority } from '../../lib/constants/priority';
import { TASK_STATUS_COLORS } from '../../lib/constants/statusColors';
import { normalizeTaskStatus, taskStatusLabel, isDone, isTodo, isInProgress, isBlocked } from '../../lib/constants/taskStatus';
import { useWorkTypes } from '../../lib/lookups/LookupContext';
import { FALLBACK_WORK_TYPES } from '../../lib/constants/workflows';
import TaskPhotoStrip from '../photos/TaskPhotoStrip';
import TaskDetailsForm from './TaskDetailsForm';
import TaskNotesSection from './TaskNotesSection';
import NotesTab from '../notes/NotesTab'; // TICKET-735 task log
import TaskTimeLogSection from './TaskTimeLogSection';
import { editContent, fetchTranslatableRow } from '../../lib/api/translations';
import { shownLanguage, TRANSLATABLE_FIELDS } from '../../../lib/quote-doc/translations.mjs';

export default function TaskDetailPanel({
  task,
  people = [],
  projects = [],
  bundles = [],
  peopleLookup = {},
  projectLookup = {},
  workTypes,
  rollups,
  onUpdate,
  onStatusChange,
  onCreateTimeEntry,
  onClose,
  onDelete,
  onTitleChanged,
  timeLogs = [],
  userRole = '',
  currentUserId = null,
}) {
  const { t } = useT();
  const { lang } = useContentLanguage();
  const { departments } = useAppDataContext();
  const dbWorkTypes = useWorkTypes();
  const dbWorkTypeLabels = dbWorkTypes.length > 0
    ? dbWorkTypes.map(wt => wt.label_en || wt.slug)
    : FALLBACK_WORK_TYPES;
  const WORK_TYPES = workTypes?.length > 0 ? workTypes : dbWorkTypeLabels;

  // TICKET-959: the task in the viewer's content language. `rowOverride` holds the
  // server's copy after a translate or an edit, so the panel never shows the parent's
  // stale row (old source + old translations) until it is reopened.
  const [rowOverride, setRowOverride] = useState(null);
  useEffect(() => { setRowOverride(null); }, [task?.id]);
  const baseTask = useMemo(() => (task ? { ...task, ...(rowOverride || {}) } : null), [task, rowOverride]);
  const baseTaskRows = useMemo(() => (baseTask ? [baseTask] : []), [baseTask]);
  const refetchTask = async () => {
    if (!task?.id) return;
    try {
      const row = await fetchTranslatableRow('tasks', task.id);
      if (row) setRowOverride(row);
    } catch { /* display keeps the source text; the toggle shows its own status */ }
  };
  const { rows: localizedTasks, translating, error: transError } = useLocalizedRows('tasks', baseTaskRows, lang, { reload: refetchTask });
  const localizedTask = localizedTasks[0] || baseTask || {};
  const inShownLanguage = !!baseTask && lang !== 'orig'
    && shownLanguage(baseTask, TRANSLATABLE_FIELDS.tasks, lang, 'es') === lang;
  const [titleError, setTitleError] = useState(null);

  const f = task || {};

  // Local status + title for the OPEN task. We track them locally and only
  // re-sync from props when the user opens a DIFFERENT task (task.id change).
  const taskIdRef = useRef(f.id);
  const [localStatus, setLocalStatus] = useState(f.status || 'todo');
  const [localTitle, setLocalTitle] = useState(f.task_name || '');
  const [localPriority, setLocalPriority] = useState(f.priority ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (taskIdRef.current !== f.id) {
      taskIdRef.current = f.id;
      setLocalStatus(f.status || 'todo');
      setLocalTitle(f.task_name || '');
      setLocalPriority(f.priority ?? null);
      setSaving(false);
    }
  }, [f.id, f.status, f.task_name, f.priority]);

  // Watchdog: if saving stuck for 8s, force-clear
  useEffect(() => {
    if (!saving) return;
    const timer = setTimeout(() => {
      console.warn('[TaskDetailPanel] saving watchdog fired — force clearing after 8s');
      setSaving(false);
    }, 8000);
    return () => clearTimeout(timer);
  }, [saving]);

  const status = localStatus;

  // TICKET-022 — replace native confirm() with state-managed two-tap.
  // Native confirm() is silently suppressed on iOS Safari inside a modal/PWA;
  // CEO live test 2026-04-26 22:33 — confirm dialog appeared but tap didn't
  // trigger soft-delete or close the card. Two-tap pattern works identically
  // across iOS Safari, desktop browsers, and JSDOM. Auto-resets after 3s
  // so a stale confirming state can't fire on an unrelated later click.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const confirmDeleteTimerRef = useRef(null);
  useEffect(() => () => {
    if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
  }, []);

  // Inline title editing
  const [editingTitle, setEditingTitle] = useState(false);
  // The title as shown: the translated copy when the viewer reads another language,
  // else the source (local state, so a same-language save shows instantly).
  const shownTitle = inShownLanguage ? (localizedTask.task_name || '') : localTitle;
  const [titleDraft, setTitleDraft] = useState(shownTitle);
  useEffect(() => { if (!editingTitle) setTitleDraft(shownTitle); }, [shownTitle, editingTitle]);

  const commitTitle = async () => {
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === shownTitle) {
      setEditingTitle(false);
      setTitleDraft(shownTitle);
      return;
    }
    setTitleError(null);
    if (inShownLanguage) {
      // TICKET-959 edit rule: an edit in the shown language becomes the source;
      // the replaced text is kept server-side. On failure keep the typed text open.
      try {
        const r = await editContent({ table: 'tasks', id: task.id, lang, edits: { task_name: trimmed } });
        if (r?.success === false || !r?.row) throw new Error(r?.error || 'save failed');
        setRowOverride(r.row);
        setLocalTitle(r.row.task_name || trimmed);
        onTitleChanged?.(r.row.task_name || trimmed);
        setEditingTitle(false);
      } catch (e) {
        setTitleError(e?.message || 'save failed');
      }
      return;
    }
    try {
      const result = await onUpdate({ task_name: trimmed });
      if (!result || result?.success !== false) {
        setLocalTitle(trimmed);
        onTitleChanged?.(trimmed);
      }
    } finally {
      setEditingTitle(false);
    }
  };

  const assigneeId = f.assignee_id || '';
  const projectId = f.project_id || '';
  const projName = f.projects?.project_name
    || (projectId ? (projectLookup[projectId] || projects.find(p => p.id === projectId)?.project_name || '') : '');

  const doUpdate = async (updates) => {
    setSaving(true);
    try {
      await onUpdate(updates);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const previous = localStatus;
    setLocalStatus(newStatus);
    setSaving(true);
    try {
      await onStatusChange(task.id, newStatus);
    } catch (e) {
      console.warn('[TaskDetailPanel] status change failed:', e?.message);
      setLocalStatus(previous);
    } finally {
      setSaving(false);
    }
  };

  // Priority mirrors status: keep a local optimistic value so the highlight
  // moves the instant the CEO taps, instead of waiting for the change to
  // round-trip through the data layer (slow on mobile online, never offline —
  // no realtime push). CEO 2026-06-06: "this should be snappy and immediately
  // change." Reverts on explicit failure.
  const handlePriorityChange = async (p) => {
    const newPriority = pToPriority(p);
    const previous = localPriority;
    setLocalPriority(newPriority);
    setSaving(true);
    try {
      const result = await onUpdate({ priority: newPriority });
      if (result && result.success === false) setLocalPriority(previous);
    } catch (e) {
      console.warn('[TaskDetailPanel] priority change failed:', e?.message);
      setLocalPriority(previous);
    } finally {
      setSaving(false);
    }
  };

  const statusSlug = normalizeTaskStatus(status);
  const handleQuickAction = () => {
    if (isTodo(statusSlug)) handleStatusChange('in_progress');
    else if (isInProgress(statusSlug)) handleStatusChange('done');
    else if (isDone(statusSlug)) handleStatusChange('in_progress');
    else if (isBlocked(statusSlug)) handleStatusChange('in_progress');
  };

  const quickLabel = isTodo(statusSlug) ? 'Start Task'
    : isInProgress(statusSlug) ? 'Mark Complete'
    : isDone(statusSlug) ? 'Reopen'
    : 'Unblock';

  if (!task) return null;

  // Quote-review tasks are tagged `quote_review:<quoteId>` (reviewTasks.mjs) —
  // one-click link back to the quote instead of hunting for it (CEO 2026-09-01).
  const quoteTag = f.work_tag;
  const quoteId = (typeof quoteTag === 'string' && quoteTag.startsWith('quote_review:'))
    ? quoteTag.slice('quote_review:'.length)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* TICKET-959 B1 — content language toggle */}
      <ContentLangToggle
        data-testid="content-lang-toggle"
        translating={translating}
        error={transError}
        testIdPrefix="task-detail-content-lang"
      />

      {quoteId && (
        <button
          type="button"
          data-testid="task-detail-open-quote"
          onClick={() => requestQuoteOpen(quoteId)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            minHeight: 44, padding: '10px 14px',
            background: 'var(--sprob, rgba(59,130,246,0.08))', color: 'var(--accent)',
            border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <FileText size={16} /> {t('taskCard.openQuote')}
        </button>
      )}
      {/* Header with title (click-to-edit) and close */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        {editingTitle ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            type="text"
            autoFocus
            data-testid="task-title-input"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
              if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(shownTitle); setTitleError(null); }
            }}
            style={{
              flex: 1, minWidth: 0, margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--t1)',
              lineHeight: 1.3, background: 'var(--card)', border: '2px solid var(--accent)',
              borderRadius: 8, padding: '6px 10px', outline: 'none', minHeight: 44,
              fontFamily: 'inherit',
            }}
          />
          {titleError && (
            <span data-testid="task-title-error" role="alert" style={{ fontSize: 'var(--fs-12)', color: 'var(--bad)' }}>
              {(() => { const v = t('contentLang.saveFailed'); return typeof v === 'string' && v !== 'contentLang.saveFailed' ? v : 'Not saved — your text is kept, try again'; })()}
            </span>
          )}
          </div>
        ) : (
          <h3
            data-testid="task-title"
            role="button"
            tabIndex={0}
            onClick={() => setEditingTitle(true)}
            onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingTitle(true); } }}
            title={t('taskCard.clickToEditTitle')}
            aria-label={t('taskCard.clickToEditTitle')}
            style={{
              margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--t1)',
              lineHeight: 1.3, flex: 1, minWidth: 0, overflowWrap: 'anywhere', cursor: 'text', padding: '4px 6px',
              borderRadius: 8, border: '2px solid transparent',
              transition: 'background 120ms, border-color 120ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--glass)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {shownTitle || 'Untitled Task'}
          </h3>
        )}
        {onDelete && (
          <button
            data-testid="task-detail-delete"
            aria-label={confirmingDelete ? 'Tap again to confirm delete' : 'Delete task'}
            onClick={() => {
              if (confirmingDelete) {
                if (confirmDeleteTimerRef.current) {
                  clearTimeout(confirmDeleteTimerRef.current);
                  confirmDeleteTimerRef.current = null;
                }
                setConfirmingDelete(false);
                onDelete(task.id);
              } else {
                setConfirmingDelete(true);
                if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
                confirmDeleteTimerRef.current = setTimeout(() => {
                  setConfirmingDelete(false);
                  confirmDeleteTimerRef.current = null;
                }, 3000);
              }
            }}
            style={{
              background: confirmingDelete ? 'var(--bad)' : 'rgba(239,68,68,0.08)',
              border: 'none',
              color: confirmingDelete ? 'var(--card)' : 'var(--bad)',
              cursor: 'pointer',
              padding: '4px 8px', borderRadius: 6, flexShrink: 0, fontSize: 11, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4,
              minHeight: 28, // smaller header chip — full 44px is in the body
            }}
          >
            <Trash2 size={13} /> {confirmingDelete ? 'Tap again to confirm' : 'Delete'}
          </button>
        )}
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--tm)', cursor: 'pointer',
          padding: 4, borderRadius: 6, flexShrink: 0,
        }}>
          <X size={20} />
        </button>
      </div>

      {/* Stage badge (read-only) */}
      {(f.stages?.label_en || f.stages?.stage_name) && (
        <div><StageBadge stage={f.stages?.label_en || f.stages?.stage_name} /></div>
      )}

      {/* TICKET-782 — a photo work item carries its image (tasks.photo_document_id
          + region). Opened outside the lightbox the ticket showed only chips; the
          anchor crop + the extra views belong here too. */}
      {f.photo_document_id && (
        <TaskPhotoStrip
          key={`photos-${task.id}`}
          task={f}
          photo={{ id: f.photo_document_id, deal_id: f.deal_id, project_id: f.project_id }}
          showAnchor
          t={t}
        />
      )}

      {/* Priority buttons */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6, letterSpacing: 0.4, textTransform: 'uppercase' }}>Priority</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['P1', 'P2', 'P3'].map(p => {
            const currentP = priorityToP(localPriority);
            const active = currentP === p;
            const tokenBg = `var(--${p.toLowerCase()}b)`;
            const tokenFg = `var(--${p.toLowerCase()})`;
            return (
              <button
                key={p}
                onClick={() => { if (!active) handlePriorityChange(p); }}
                style={{
                  padding: '7px 18px', borderRadius: 99, fontSize: 12, minHeight: 44,
                  fontWeight: active ? 700 : 500,
                  background: active ? tokenBg : 'var(--card)',
                  color: active ? tokenFg : 'var(--tm)',
                  border: active ? `2px solid ${tokenFg}` : '1px solid var(--bdr)',
                  cursor: 'pointer',
                }}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Status buttons */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6, letterSpacing: 0.4, textTransform: 'uppercase' }}>Status</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['todo', 'in_progress', 'blocked', 'done'].map(s => {
            const sc = TASK_STATUS_COLORS[s];
            const active = statusSlug === s;
            return (
              <button key={s} onClick={() => !active && handleStatusChange(s)} style={{
                padding: '7px 14px', borderRadius: 99, fontSize: 12, minHeight: 44,
                fontWeight: active ? 600 : 400,
                background: active ? sc + '18' : 'var(--card)',
                color: active ? sc : 'var(--tm)',
                border: active ? `2px solid ${sc}` : '1px solid var(--bdr)',
                opacity: saving ? 0.6 : 1, cursor: 'pointer',
              }}>{taskStatusLabel(s)}</button>
            );
          })}
        </div>
      </div>

      {/* Details: budget, dates, assignee, department, bundle */}
      <TaskDetailsForm
        key={`details-${task.id}`}
        task={task}
        people={people}
        projects={projects}
        bundles={bundles}
        peopleLookup={peopleLookup}
        projectLookup={projectLookup}
        departments={departments}
        rollups={rollups}
        saving={saving}
        onUpdate={doUpdate}
      />

      {/* Created date */}
      {task?.createdTime && (
        <div style={{ fontSize: 11, color: 'var(--tm)', textAlign: 'center', padding: '4px 0' }}>
          Created {new Date(task.createdTime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          {f.task_number ? ` · #${f.task_number}` : ''}
        </div>
      )}

      {/* Quick action */}
      <button onClick={handleQuickAction} disabled={saving} style={{
        padding: 14, borderRadius: 14, fontSize: 15, fontWeight: 600, minHeight: 48,
        background: saving ? 'var(--bdr)' : 'var(--navy-surface)',
        color: saving ? 'var(--tm)' : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        cursor: 'pointer', border: 'none',
      }}>
        <CheckSquare size={18} />
        {saving ? 'Saving...' : quickLabel}
      </button>

      {/* Time logging */}
      <TaskTimeLogSection
        key={`timelog-${task.id}`}
        task={task}
        projectId={projectId}
        assigneeId={assigneeId}
        workTypes={WORK_TYPES}
        timeLogs={timeLogs}
        peopleLookup={peopleLookup}
        onCreateTimeEntry={onCreateTimeEntry}
      />

      {/* Notes, materials, voice, photo */}
      <TaskNotesSection
        key={`notes-${task.id}`}
        task={task}
        statusSlug={statusSlug}
        projectId={projectId}
        projName={projName}
        saving={saving}
        onUpdate={doUpdate}
      />

      {/* TICKET-735 — task log: blog-style thread (daily notes / missing / reminders) */}
      {task?.id && (
        <div data-testid="task-log-section" style={{ borderTop: '1px solid var(--bdr)', paddingTop: 12 }}>
          <NotesTab scope="task" scopeId={task.id} projectId={projectId} />
        </div>
      )}
    </div>
  );
}
